import React, { useState, useEffect } from 'react';
import { Search, Trophy, TrendingUp, Filter, Plus, Edit2, Trash2, ExternalLink, RefreshCw, ChevronLeft, ChevronRight, User as UserIcon, X, Loader2, AlertTriangle } from 'lucide-react';
import { Language } from '../../../types';
import { at } from '../../../i18nAdmin';
import { t } from '../../../i18n';
import { supabase } from '../../../services/authService';

interface TransferGroup {
  id: string;
  type: 'latest' | 'top';
  season?: string;
  window?: 'all' | 'summer' | 'winter';
  created_at: string;
}

interface TransferItem {
  id: string;
  group_id: string;
  name: string;
  player: string;
  player_id?: string;
  playerface?: string;
  leftteam?: string;
  leftemblem?: string;
  joinedteam?: string;
  joinedemblem?: string;
  fee?: string;
  marketvalue?: string;
  transferdate?: string;
  leaguegroup?: string;
}

interface PlayersTabProps {
  lang: Language;
}

export const PlayersTab: React.FC<PlayersTabProps> = ({ lang }) => {
  const [groups, setGroups] = useState<TransferGroup[]>([]);
  const [items, setItems] = useState<TransferItem[]>([]);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeType, setActiveType] = useState<'latest' | 'top'>('latest');

  // Modal States
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [isGroupAnimating, setIsGroupAnimating] = useState(false);
  const [isItemModalOpen, setIsItemModalOpen] = useState(false);
  const [isItemAnimating, setIsItemAnimating] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [confirmType, setConfirmType] = useState<'deleteGroup' | 'deleteItem'>('deleteGroup');
  
  const [editingGroup, setEditingGroup] = useState<Partial<TransferGroup> | null>(null);
  const [editingItem, setEditingItem] = useState<Partial<TransferItem> | null>(null);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [groupToDelete, setGroupToDelete] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    fetchGroups();
  }, [activeType]);

  useEffect(() => {
    if (selectedGroupId) {
      fetchItems(selectedGroupId);
    } else {
      setItems([]);
    }
  }, [selectedGroupId]);

  const fetchGroups = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('transfer_groups')
        .select('*')
        .eq('type', activeType)
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      setGroups(data || []);
      if (data && data.length > 0) {
        setSelectedGroupId(data[0].id);
      } else {
        setSelectedGroupId(null);
      }
    } catch (err) {
      console.error('Error fetching groups:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchItems = async (groupId: string) => {
    try {
      const { data, error } = await supabase
        .from('transfer_items')
        .select('*')
        .eq('group_id', groupId)
        .order('id', { ascending: true });
      
      if (error) throw error;
      setItems(data || []);
    } catch (err) {
      console.error('Error fetching items:', err);
    }
  };

  const handleSaveGroup = async () => {
    if (!editingGroup?.type || isSubmitting) return;
    setIsSubmitting(true);
    try {
      // Trigger Oracle API sync
      const oracleHost = import.meta.env.VITE_ORACLE_HOST;
      const oracleKey = import.meta.env.VITE_ORACLE_ADMIN_KEY;
      
      if (oracleKey) {
        let apiUrl = '';
        if (editingGroup.type === 'latest') {
          apiUrl = `${oracleHost}/api/run-latest-transfers`;
        } else if (editingGroup.type === 'top') {
          const yearMatch = editingGroup.season?.match(/\d{4}/) || editingGroup.season?.match(/\d{2}/);
          let year = yearMatch ? yearMatch[0] : new Date().getFullYear().toString();
          if (year.length === 2) year = `20${year}`;
          
          const windowParam = editingGroup.window || 'all';
          apiUrl = `${oracleHost}/api/run-top-transfers/${year}?window=${windowParam}`;
        }

        if (apiUrl) {
          // Change to use proxy or handle CORS if needed, but here we just ensure the URL and headers are correct
          const response = await fetch(apiUrl, {
            method: 'GET',
            mode: 'cors',
            headers: {
              'x-api-key': oracleKey,
              'Accept': 'application/json'
            }
          });
          
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Oracle API failed (${response.status}): ${errorText || response.statusText}`);
          }
          
          // API success - close modal and refresh list
          await fetchGroups();
          closeGroupModal();
        } else {
          throw new Error('Invalid API configuration');
        }
      } else {
        throw new Error('Missing Oracle configuration');
      }
    } catch (err) {
      console.error('Error in handleSaveGroup:', err);
      // 에러 메시지가 'Failed to fetch'인 경우 네트워크 문제나 CORS 문제일 가능성이 큼
      const errorMessage = err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.';
      alert(`Error: ${errorMessage}\n\n서버 연결에 실패했습니다. API 서버 상태와 CORS 설정을 확인해주세요.`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveItem = async () => {
    if ((!editingItem?.player && !editingItem?.name) || !selectedGroupId) return;
    setIsSubmitting(true);
    try {
      const payload: any = {
        group_id: selectedGroupId,
        player: editingItem.player || editingItem.name,
        player_id: editingItem.player_id,
        playerface: editingItem.playerface,
        leftteam: editingItem.leftteam,
        leftemblem: editingItem.leftemblem,
        joinedteam: editingItem.joinedteam,
        joinedemblem: editingItem.joinedemblem,
        fee: editingItem.fee,
        marketvalue: editingItem.marketvalue,
        leaguegroup: editingItem.leaguegroup,
        transferdate: editingItem.transferdate || new Date().toISOString()
      };

      let error;
      if (modalMode === 'create') {
        ({ error } = await supabase.from('transfer_items').insert([payload]));
      } else {
        ({ error } = await supabase.from('transfer_items').update(payload).eq('id', editingItem.id));
      }

      if (error) throw error;
      fetchItems(selectedGroupId);
      closeItemModal();
    } catch (err) {
      console.error('Error saving item:', err);
      const errorMessage = err instanceof Error ? err.message : '저장 중 오류가 발생했습니다.';
      alert(`Error: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsSubmitting(true);
    try {
      if (confirmType === 'deleteGroup' && groupToDelete) {
        // Cascade delete is handled by database or manually if needed
        // But the request says "그룹 삭제 시 그룹안에 모든 선수들도 삭제 되게 해줘"
        // Let's do it manually just in case DB doesn't have CASCADE
        await supabase.from('transfer_items').delete().eq('group_id', groupToDelete);
        const { error } = await supabase.from('transfer_groups').delete().eq('id', groupToDelete);
        if (error) throw error;
        fetchGroups();
      } else if (confirmType === 'deleteItem' && itemToDelete) {
        const { error } = await supabase.from('transfer_items').delete().eq('id', itemToDelete);
        if (error) throw error;
        if (selectedGroupId) fetchItems(selectedGroupId);
      }
      setIsConfirmModalOpen(false);
    } catch (err) {
      console.error('Error deleting:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const openGroupModal = (mode: 'create' | 'edit', group?: TransferGroup) => {
    setModalMode(mode);
    setEditingGroup(group || { type: activeType, season: '', window: 'all' });
    setIsGroupModalOpen(true);
    setTimeout(() => setIsGroupAnimating(true), 10);
  };

  const closeGroupModal = () => {
    setIsGroupAnimating(false);
    setTimeout(() => {
      setIsGroupModalOpen(false);
      setEditingGroup(null);
    }, 400);
  };

  const openItemModal = (mode: 'create' | 'edit', item?: TransferItem) => {
    setModalMode(mode);
    setEditingItem(item || { 
      player: '', 
      name: '', 
      player_id: '', 
      playerface: '', 
      leftteam: '', 
      leftemblem: '', 
      joinedteam: '', 
      joinedemblem: '', 
      fee: '', 
      marketvalue: '', 
      leaguegroup: '' 
    });
    setIsItemModalOpen(true);
    setTimeout(() => setIsItemAnimating(true), 10);
  };

  const closeItemModal = () => {
    setIsItemAnimating(false);
    setTimeout(() => {
      setIsItemModalOpen(false);
      setEditingItem(null);
    }, 400);
  };

  const openDeleteConfirm = (type: 'deleteGroup' | 'deleteItem', id: string) => {
    setConfirmType(type);
    if (type === 'deleteGroup') setGroupToDelete(id);
    else setItemToDelete(id);
    setIsConfirmModalOpen(true);
  };

  const filteredItems = items
    .filter(item => 
      item.player?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.joinedteam?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.leftteam?.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => Number(a.id) - Number(b.id));

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-8">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 tracking-tighter uppercase">
            {at(lang, 'playerAndTransferManagement')}
          </h1>
          <p className="text-sm font-medium text-slate-500 mt-1">
            {at(lang, 'playerManagementDesc')}
          </p>
        </div>
        <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-2xl">
          <button 
            onClick={() => setActiveType('latest')}
            className={`px-6 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${activeType === 'latest' ? 'bg-white text-rose-500 shadow-sm' : 'text-slate-400 md:hover:text-slate-600'}`}
          >
            {at(lang, 'latestTransfers')}
          </button>
          <button 
            onClick={() => setActiveType('top')}
            className={`px-6 py-2.5 rounded-xl text-[11px] font-black uppercase tracking-widest transition-all ${activeType === 'top' ? 'bg-white text-emerald-500 shadow-sm' : 'text-slate-400 md:hover:text-slate-600'}`}
          >
            {at(lang, 'topTransfers')}
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Left: Groups Sidebar */}
        <div className="lg:col-span-1 space-y-4">
          <div className="flex items-center justify-between px-2">
            <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em]">
              {at(lang, 'groupList')}
            </h3>
            <button 
              onClick={() => openGroupModal('create')}
              className="p-1.5 text-slate-400 md:hover:text-rose-500 transition-colors"
            >
              <Plus size={16} />
            </button>
          </div>
          <div className="space-y-2 max-h-[600px] overflow-y-auto no-scrollbar pr-2">
            {groups.map(group => (
              <div key={group.id} className="relative group/group-card">
                <button
                  onClick={() => setSelectedGroupId(group.id)}
                  className={`w-full flex flex-col items-start p-4 rounded-2xl border transition-all text-left ${selectedGroupId === group.id ? 'bg-slate-900 border-slate-900 shadow-lg shadow-slate-200' : 'bg-white border-slate-200 md:hover:border-slate-300'}`}
                >
                  <div className="flex items-center justify-between w-full mb-1">
                    <span className={`text-[9px] font-black uppercase tracking-widest ${selectedGroupId === group.id ? 'text-rose-400' : 'text-rose-500'}`}>
                      ID: {group.id}
                    </span>
                    {group.season && (
                      <span className={`text-[9px] font-black px-1.5 py-0.5 rounded ${selectedGroupId === group.id ? 'bg-white/10 text-white' : 'bg-slate-100 text-slate-500'}`}>
                        {group.season}
                      </span>
                    )}
                  </div>
                  <span className={`text-sm font-bold truncate w-full ${selectedGroupId === group.id ? 'text-white' : 'text-slate-900'}`}>
                    {group.type === 'latest' ? 'Market Movements' : 'Top Tier Assets'}
                  </span>
                  <span className={`text-[10px] mt-1 font-medium ${selectedGroupId === group.id ? 'text-slate-400' : 'text-slate-400'}`}>
                    {new Date(group.created_at).toLocaleDateString()}
                  </span>
                </button>
                <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover/group-card:opacity-100 transition-opacity">
                   <button 
                    onClick={(e) => { e.stopPropagation(); openGroupModal('edit', group); }}
                    className={`p-1.5 rounded-lg border shadow-sm transition-all ${selectedGroupId === group.id ? 'bg-white/10 border-white/20 text-white md:hover:bg-white/20' : 'bg-white border-slate-200 text-slate-400 md:hover:text-slate-900'}`}
                   >
                     <Edit2 size={12} />
                   </button>
                   <button 
                    onClick={(e) => { e.stopPropagation(); openDeleteConfirm('deleteGroup', group.id); }}
                    className={`p-1.5 rounded-lg border shadow-sm transition-all ${selectedGroupId === group.id ? 'bg-white/10 border-white/20 text-white md:hover:bg-rose-500 md:hover:border-rose-500' : 'bg-white border-slate-200 text-slate-400 md:hover:text-rose-600'}`}
                   >
                     <Trash2 size={12} />
                   </button>
                </div>
              </div>
            ))}
            {groups.length === 0 && !isLoading && (
              <div className="py-12 text-center bg-slate-50 rounded-2xl border border-dashed border-slate-200">
                <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">No groups found</p>
              </div>
            )}
          </div>
        </div>

        {/* Right: Items Table */}
        <div className="lg:col-span-3 space-y-6">
          <div className="bg-white rounded-[32px] border border-slate-200 shadow-sm overflow-hidden">
            <div className="p-6 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                <input 
                  type="text" 
                  placeholder={at(lang, 'playerSearchPlaceholder')} 
                  className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3 pl-12 pr-4 text-sm font-bold text-slate-900 placeholder:text-slate-300 outline-none focus:bg-white focus:ring-2 focus:ring-rose-500/10 transition-all"
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                />
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => openItemModal('create')}
                  className="flex items-center gap-2 px-4 py-2.5 bg-rose-500 text-white rounded-xl text-[11px] font-black uppercase tracking-widest md:hover:bg-rose-600 transition-all shadow-md active:scale-95"
                >
                  <Plus size={14} />
                  {at(lang, 'addItem')}
                </button>
                <button 
                  onClick={() => selectedGroupId && fetchItems(selectedGroupId)}
                  className="p-2.5 bg-white border border-slate-200 text-slate-400 md:hover:text-slate-900 rounded-xl transition-all active:rotate-180"
                >
                  <RefreshCw size={18} />
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-slate-50/50 text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] border-b border-slate-100">
                    <th className="px-6 py-5 min-w-[200px]">{at(lang, 'playerIntel')}</th>
                    <th className="px-6 py-5 min-w-[150px]">{at(lang, 'movement')}</th>
                    <th className="px-6 py-5 min-w-[120px]">{at(lang, 'valueFee')}</th>
                    <th className="px-6 py-5 text-right">{at(lang, 'ops')}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filteredItems.map(item => (
                    <tr key={item.id} className="md:hover:bg-slate-50/50 transition-colors group">
                      <td className="px-6 py-5">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 rounded-full overflow-hidden border border-slate-100 bg-slate-50 shrink-0">
                            <img 
                              src={item.playerface || `https://ui-avatars.com/api/?name=${encodeURIComponent(item.player || item.name)}&background=f1f5f9&color=cbd5e1&bold=true`} 
                              className="w-full h-full object-cover" 
                              alt="" 
                            />
                          </div>
                          <div className="flex flex-col min-w-0">
                            <span className="text-sm font-black text-slate-900 truncate uppercase tracking-tight md:group-hover:text-rose-500 transition-colors">
                              {item.player || item.name}
                            </span>
                            <span className="text-[10px] text-slate-400 font-bold truncate">
                              {item.leaguegroup || 'Global Sector'}
                            </span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex flex-col gap-1.5">
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 flex items-center justify-center shrink-0">
                              <img src={item.leftemblem} className="max-w-full max-h-full object-contain opacity-40" alt="" />
                            </div>
                            <span className="text-[11px] font-bold text-slate-400 truncate">{item.leftteam}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="w-5 h-5 flex items-center justify-center shrink-0">
                              <img src={item.joinedemblem} className="max-w-full max-h-full object-contain" alt="" />
                            </div>
                            <span className="text-[11px] font-black text-slate-900 truncate uppercase tracking-tight">{item.joinedteam}</span>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-5">
                        <div className="flex flex-col">
                          <span className="text-xs font-black text-rose-600 tabular-nums">
                            {item.fee || 'Undisclosed'}
                          </span>
                          <span className="text-[10px] font-bold text-slate-400 mt-0.5">
                            Val: {item.marketvalue || '-'}
                          </span>
                        </div>
                      </td>
                      <td className="px-6 py-5 text-right">
                        <div className="flex items-center justify-end gap-2">
                          <button 
                            onClick={() => openItemModal('edit', item)}
                            className="p-2 bg-white border border-slate-200 text-slate-400 md:hover:text-slate-900 md:hover:border-slate-900 rounded-lg transition-all shadow-sm"
                          >
                            <Edit2 size={14} />
                          </button>
                          <button 
                            onClick={() => openDeleteConfirm('deleteItem', item.id)}
                            className="p-2 bg-white border border-slate-200 text-slate-400 md:hover:text-rose-600 md:hover:border-rose-600 rounded-lg transition-all shadow-sm"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filteredItems.length === 0 && (
                    <tr>
                      <td colSpan={4} className="px-6 py-20 text-center">
                        <div className="flex flex-col items-center justify-center opacity-30 gap-4">
                          <TrendingUp size={48} className="text-slate-200" />
                          <p className="text-sm font-black uppercase tracking-widest">
                            {at(lang, 'noItemsFound')}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Group Modal */}
      {isGroupModalOpen && (
        <div className={`fixed inset-0 z-[500] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-300 !m-0 ${isGroupAnimating ? 'opacity-100' : 'opacity-0'}`}>
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={closeGroupModal}></div>
          <div className={`relative bg-white w-full max-w-md rounded-t-[32px] md:rounded-[32px] shadow-2xl overflow-hidden transition-all duration-500 ease-[0.32,0.72,0,1] ${isGroupAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
            <div className="p-8">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                  {modalMode === 'create' ? at(lang, 'createGroup') : at(lang, 'editGroup')}
                </h3>
                <button onClick={closeGroupModal} className="text-slate-400 md:hover:text-slate-900 transition-colors">
                  <X size={24} />
                </button>
              </div>
              
              <div className="space-y-6">
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Type</label>
                  <div className="grid grid-cols-2 gap-2 p-1 bg-slate-100 rounded-2xl">
                    <button 
                      onClick={() => setEditingGroup(prev => ({ ...prev, type: 'latest' }))}
                      className={`py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${editingGroup?.type === 'latest' ? 'bg-white text-rose-500 shadow-sm' : 'text-slate-400'}`}
                    >
                      Latest
                    </button>
                    <button 
                      onClick={() => setEditingGroup(prev => ({ ...prev, type: 'top' }))}
                      className={`py-2.5 rounded-xl text-[10px] font-black uppercase tracking-widest transition-all ${editingGroup?.type === 'top' ? 'bg-white text-emerald-500 shadow-sm' : 'text-slate-400'}`}
                    >
                      Top
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Season (Year)</label>
                  <input 
                    type="text" 
                    className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3 px-4 text-sm font-bold text-slate-900 outline-none focus:bg-white focus:ring-2 focus:ring-rose-500/10 transition-all"
                    placeholder="e.g. 2026"
                    value={editingGroup?.season || ''}
                    onChange={e => setEditingGroup(prev => ({ ...prev, season: e.target.value }))}
                  />
                </div>

                {editingGroup?.type === 'top' && (
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-2">Transfer Window</label>
                    <select 
                      className="w-full bg-slate-50 border border-slate-100 rounded-xl py-3 px-4 text-sm font-bold text-slate-900 outline-none focus:bg-white focus:ring-2 focus:ring-rose-500/10 transition-all"
                      value={editingGroup?.window || 'all'}
                      onChange={e => setEditingGroup(prev => ({ ...prev, window: e.target.value as any }))}
                    >
                      <option value="all">All (전체)</option>
                      <option value="summertransfers">Summer (여름)</option>
                      <option value="wintertransfers">Winter (겨울)</option>
                    </select>
                  </div>
                )}
              </div>

              <div className="mt-10 flex gap-3">
                <button onClick={closeGroupModal} className="flex-1 py-4 text-xs font-black text-slate-400 uppercase tracking-widest">
                  {at(lang, 'cancel')}
                </button>
                <button 
                  onClick={handleSaveGroup}
                  disabled={isSubmitting}
                  className="flex-[2] py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-slate-100 md:hover:bg-black transition-all active:scale-95 disabled:opacity-50"
                >
                  {isSubmitting ? <Loader2 className="animate-spin mx-auto" size={18} /> : (at(lang, 'save'))}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Item Modal */}
      {isItemModalOpen && (
        <div className={`fixed inset-0 z-[500] flex flex-col justify-end md:items-center md:justify-center transition-opacity duration-300 !m-0 ${isItemAnimating ? 'opacity-100' : 'opacity-0'}`}>
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={closeItemModal}></div>
          <div className={`relative bg-white w-full max-w-2xl rounded-t-[32px] md:rounded-[32px] shadow-2xl overflow-hidden transition-all duration-500 ease-[0.32,0.72,0,1] ${isItemAnimating ? 'translate-y-0' : 'translate-y-full'}`}>
            <div className="p-8">
              <div className="flex items-center justify-between mb-8">
                <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight">
                  {modalMode === 'create' ? at(lang, 'addPersonnel') : at(lang, 'editPersonnel')}
                </h3>
                <button onClick={closeItemModal} className="text-slate-400 md:hover:text-slate-900 transition-colors">
                  <X size={24} />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Player Name</label>
                    <input 
                      type="text" 
                      className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2.5 px-4 text-sm font-bold outline-none focus:bg-white transition-all"
                      value={editingItem?.player || editingItem?.name || ''}
                      onChange={e => setEditingItem(prev => ({ ...prev, player: e.target.value, name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Player ID (API-Sports)</label>
                    <input 
                      type="text" 
                      className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2.5 px-4 text-sm font-bold outline-none focus:bg-white transition-all"
                      value={editingItem?.player_id || ''}
                      onChange={e => setEditingItem(prev => ({ ...prev, player_id: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Image URL</label>
                    <input 
                      type="text" 
                      className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2.5 px-4 text-sm font-bold outline-none focus:bg-white transition-all"
                      value={editingItem?.playerface || ''}
                      onChange={e => setEditingItem(prev => ({ ...prev, playerface: e.target.value }))}
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">League Group</label>
                    <input 
                      type="text" 
                      className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2.5 px-4 text-sm font-bold outline-none focus:bg-white transition-all"
                      value={editingItem?.leaguegroup || ''}
                      onChange={e => setEditingItem(prev => ({ ...prev, leaguegroup: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Left Club</label>
                      <input 
                        type="text" 
                        className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2.5 px-4 text-sm font-bold outline-none focus:bg-white transition-all"
                        value={editingItem?.leftteam || ''}
                        onChange={e => setEditingItem(prev => ({ ...prev, leftteam: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Joined Team</label>
                      <input 
                        type="text" 
                        className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2.5 px-4 text-sm font-bold outline-none focus:bg-white transition-all"
                        value={editingItem?.joinedteam || ''}
                        onChange={e => setEditingItem(prev => ({ ...prev, joinedteam: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Left Emblem</label>
                      <input 
                        type="text" 
                        className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2.5 px-4 text-sm font-bold outline-none focus:bg-white transition-all"
                        value={editingItem?.leftemblem || ''}
                        onChange={e => setEditingItem(prev => ({ ...prev, leftemblem: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Joined Emblem</label>
                      <input 
                        type="text" 
                        className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2.5 px-4 text-sm font-bold outline-none focus:bg-white transition-all"
                        value={editingItem?.joinedemblem || ''}
                        onChange={e => setEditingItem(prev => ({ ...prev, joinedemblem: e.target.value }))}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Fee</label>
                      <input 
                        type="text" 
                        className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2.5 px-4 text-sm font-bold outline-none focus:bg-white transition-all"
                        value={editingItem?.fee || ''}
                        onChange={e => setEditingItem(prev => ({ ...prev, fee: e.target.value }))}
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1.5">Market Value</label>
                      <input 
                        type="text" 
                        className="w-full bg-slate-50 border border-slate-100 rounded-xl py-2.5 px-4 text-sm font-bold outline-none focus:bg-white transition-all"
                        value={editingItem?.marketvalue || ''}
                        onChange={e => setEditingItem(prev => ({ ...prev, marketvalue: e.target.value }))}
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-10 flex gap-3">
                <button onClick={closeItemModal} className="flex-1 py-4 text-xs font-black text-slate-400 uppercase tracking-widest">
                  {at(lang, 'cancel')}
                </button>
                <button 
                  onClick={handleSaveItem}
                  disabled={isSubmitting}
                  className="flex-[2] py-4 bg-slate-900 text-white rounded-2xl font-black text-xs uppercase tracking-widest shadow-xl shadow-slate-100 md:hover:bg-black transition-all active:scale-95 disabled:opacity-50"
                >
                  {isSubmitting ? <Loader2 className="animate-spin mx-auto" size={18} /> : (at(lang, 'save'))}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Modal */}
      {isConfirmModalOpen && (
        <div className="fixed inset-0 z-[550] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={() => setIsConfirmModalOpen(false)}></div>
          <div className="relative bg-white w-full max-w-sm rounded-[32px] shadow-2xl p-8 animate-in zoom-in-95 duration-200">
            <div className="w-14 h-14 rounded-2xl bg-rose-50 text-rose-500 flex items-center justify-center mb-6">
              <AlertTriangle size={28} />
            </div>
            <h3 className="text-xl font-black text-slate-900 uppercase tracking-tight mb-2">
              {confirmType === 'deleteGroup' ? at(lang, 'deleteGroup') : at(lang, 'deleteItem')}
            </h3>
            <p className="text-sm text-slate-500 font-medium leading-relaxed mb-8">
              {confirmType === 'deleteGroup' 
                ? at(lang, 'deleteGroupConfirmDesc')
                : at(lang, 'deleteItemConfirmDesc')
              }
            </p>
            <div className="flex gap-3">
              <button onClick={() => setIsConfirmModalOpen(false)} className="flex-1 py-3 text-xs font-black text-slate-400 uppercase tracking-widest">
                {at(lang, 'cancel')}
              </button>
              <button 
                onClick={handleDeleteConfirm}
                disabled={isSubmitting}
                className="flex-[2] py-3 bg-rose-500 text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-lg shadow-rose-100 md:hover:bg-rose-600 transition-all active:scale-95 disabled:opacity-50"
              >
                {isSubmitting ? <Loader2 className="animate-spin mx-auto" size={18} /> : (at(lang, 'delete'))}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
